import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Lock, Globe, RefreshCw, Trash2, Star, Trophy } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import Header from '../../components/layout/Header';
import Button from '../../components/ui/Button';
import CityAutocomplete from '../../components/ui/CityAutocomplete';
import Modal from '../../components/modals/Modal';
import NumericInput from '../../components/ui/NumericInput';

const GroupFormPage = () => {
  const { groups, addGroup, updateGroup, deleteGroup, addGame } = useAppContext();
  const navigate = useNavigate();
  const { id } = useParams();
  const editingGroup = id ? groups.find(g => g.id === id) : null;
  const [isPrivate, setIsPrivate] = useState(editingGroup?.isPrivate || false);
  const [isRecurring, setIsRecurring] = useState(editingGroup?.isRecurring !== false);
  const [city, setCity] = useState(editingGroup?.city || '');
  const [skipVoting, setSkipVoting] = useState(editingGroup?.skipVoting || false);
  const [scoringEnabled, setScoringEnabled] = useState(editingGroup?.scoringConfig?.enabled || false);
  const [price, setPrice] = useState(Number(editingGroup?.defaultPrice) || 20);
  const [maxPlayers, setMaxPlayers] = useState(Number(editingGroup?.defaultMaxPlayers) || 14);
  const [scoringWin, setScoringWin] = useState(editingGroup?.scoringConfig?.win ?? 3);
  const [scoringDraw, setScoringDraw] = useState(editingGroup?.scoringConfig?.draw ?? 1);
  const [scoringAbsence, setScoringAbsence] = useState(editingGroup?.scoringConfig?.absencePenalty ?? 0);
  const [scoringLeaderZone, setScoringLeaderZone] = useState(editingGroup?.scoringConfig?.leaderZone ?? 2);
  const [scoringRelegZone, setScoringRelegZone] = useState(editingGroup?.scoringConfig?.relegationZone ?? 2);
  const [scoringRequireStats, setScoringRequireStats] = useState(editingGroup?.scoringConfig?.requireStats || false);
  const [checkinHoursBefore, setCheckinHoursBefore] = useState(Number(editingGroup?.checkinHoursBefore) || 0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Calcula próxima data para um dia da semana
  const getNextDateForDay = (dayOfWeek) => {
    const days = { dom: 0, seg: 1, ter: 2, qua: 3, qui: 4, sex: 5, sab: 6 };
    const target = days[dayOfWeek];
    if (target === undefined) return new Date().toISOString().split('T')[0];
    const today = new Date();
    const current = today.getDay();
    let diff = target - current;
    if (diff <= 0) diff += 7;
    const next = new Date(today);
    next.setDate(today.getDate() + diff);
    return next.toISOString().split('T')[0];
  };

  const geocodeGroup = async (courtAddress, cityName) => {
    const query = courtAddress ? `${courtAddress}, ${cityName}` : cityName;
    if (!query.trim()) return null;
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`,
        { headers: { 'User-Agent': 'PeladaMatch/1.0' } }
      );
      const data = await res.json();
      if (data[0]) return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
    } catch (_e) {
      // não bloquear o save se geocodificação falhar
    }
    return null;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    const scoring = {
      enabled: scoringEnabled,
      win: Number(scoringWin) || 3,
      draw: Number(scoringDraw) || 1,
      absencePenalty: Number(scoringAbsence) || 0,
      leaderZone: Number(scoringLeaderZone) || 2,
      relegationZone: Number(scoringRelegZone) || 2,
      requireStats: scoringRequireStats,
    };

    // Geocodificar endereço
    const geoCoords = await geocodeGroup(data.courtAddress, data.city);

    if (editingGroup) {
      const groupUpdates = {
        name: data.name,
        sport: data.sport,
        isPrivate,
        isRecurring,
        skipVoting,
        scoringConfig: scoring,
        checkinHoursBefore: Number(checkinHoursBefore) || 0,
        paymentFrequency: data.paymentFrequency,
        dayOfWeek: data.dayOfWeek,
        city: data.city,
        courtName: data.courtName || '',
        courtAddress: data.courtAddress || '',
        defaultTime: data.time,
        defaultPrice: price,
        defaultMaxPlayers: maxPlayers,
      };
      if (geoCoords) { groupUpdates.lat = geoCoords.lat; groupUpdates.lng = geoCoords.lng; }
      await updateGroup(editingGroup.id, groupUpdates);
    } else {
      const firstDate = getNextDateForDay(data.dayOfWeek);
      const groupData = {
        name: data.name,
        sport: data.sport,
        isPrivate,
        isRecurring,
        skipVoting,
        scoringConfig: scoring,
        checkinHoursBefore: Number(checkinHoursBefore) || 0,
        paymentFrequency: data.paymentFrequency,
        dayOfWeek: data.dayOfWeek,
        city: data.city,
        courtName: data.courtName || '',
        courtAddress: data.courtAddress || '',
        defaultTime: data.time,
        defaultPrice: price,
        defaultMaxPlayers: maxPlayers,
      };
      if (geoCoords) { groupData.lat = geoCoords.lat; groupData.lng = geoCoords.lng; }
      const groupId = await addGroup(groupData);
      // Criar primeiro jogo automaticamente
      await addGame(groupId, {
        date: firstDate,
        time: data.time,
        maxPlayers,
        playersPerTeam: Math.floor(maxPlayers / 2) - 1,
        pricePerPerson: price,
      });
    }
    navigate('/');
  };

  return (
    <>
      <Header showBack onBack={() => navigate('/')} />
      <div className="p-6 flex-1 overflow-y-auto">
        <h2 className="text-2xl font-bold text-slate-800 mb-6">{editingGroup ? 'Editar Grupo' : 'Criar Nova Pelada'}</h2>
        <form onSubmit={handleSave} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nome</label>
            <input name="name" defaultValue={editingGroup?.name} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Esporte</label>
            <select name="sport" defaultValue={editingGroup?.sport || 'Futebol Society'} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none">
              <option>Futebol Society</option>
              <option>Futsal</option>
              <option>Futebol de Campo</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Cidade *</label>
            <CityAutocomplete value={city} onChange={setCity} required />
            <input type="hidden" name="city" value={city} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nome da Quadra</label>
              <input name="courtName" defaultValue={editingGroup?.courtName} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none" placeholder="Ex: Arena Society" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Endereço (abre no Maps)</label>
              <input name="courtAddress" defaultValue={editingGroup?.courtAddress} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none" placeholder="Ex: Av. Paulista, 1000, São Paulo" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Dia da Semana</label>
              <select name="dayOfWeek" defaultValue={editingGroup?.dayOfWeek || 'ter'} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none">
                <option value="seg">Segunda-feira</option>
                <option value="ter">Terça-feira</option>
                <option value="qua">Quarta-feira</option>
                <option value="qui">Quinta-feira</option>
                <option value="sex">Sexta-feira</option>
                <option value="sab">Sábado</option>
                <option value="dom">Domingo</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Horário</label>
              <input type="time" name="time" required defaultValue={editingGroup?.defaultTime || "20:00"} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Preço (R$)</label>
              <NumericInput value={price} onChange={setPrice} min={0} max={9999} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Recorrência</label>
              <select name="paymentFrequency" defaultValue={editingGroup?.paymentFrequency || 'weekly'} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none text-sm">
                <option value="weekly">Semanal</option>
                <option value="monthly">Mensal</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Máx Jogadores</label>
            <NumericInput value={maxPlayers} onChange={setMaxPlayers} min={2} max={99} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none" />
          </div>
          <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              {isPrivate ? <Lock className="w-5 h-5 text-slate-500" /> : <Globe className="w-5 h-5 text-blue-600" />}
              <div>
                <div className="text-sm font-medium text-slate-700">{isPrivate ? 'Grupo Privado' : 'Grupo Público'}</div>
                <div className="text-xs text-slate-400">{isPrivate ? 'Só membros convidados' : 'Qualquer jogador pode entrar'}</div>
              </div>
            </div>
            <button type="button" onClick={() => setIsPrivate(!isPrivate)} className={`relative w-12 h-7 rounded-full transition-colors ${isPrivate ? 'bg-slate-300' : 'bg-blue-500'}`}>
              <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${isPrivate ? 'left-0.5' : 'left-[22px]'}`} />
            </button>
          </div>
          <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <RefreshCw className={`w-5 h-5 ${isRecurring ? 'text-blue-600' : 'text-slate-500'}`} />
              <div>
                <div className="text-sm font-medium text-slate-700">{isRecurring ? 'Jogo Recorrente' : 'Jogo Único'}</div>
                <div className="text-xs text-slate-400">{isRecurring ? 'Cria próximo jogo ao finalizar' : 'Sem repetição automática'}</div>
              </div>
            </div>
            <button type="button" onClick={() => setIsRecurring(!isRecurring)} className={`relative w-12 h-7 rounded-full transition-colors ${isRecurring ? 'bg-blue-500' : 'bg-slate-300'}`}>
              <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${isRecurring ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
          </div>
          <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Star className={`w-5 h-5 ${!skipVoting ? 'text-amber-500' : 'text-slate-500'}`} />
              <div>
                <div className="text-sm font-medium text-slate-700">{skipVoting ? 'Finalizar Direto' : 'Avaliação Pós-Jogo'}</div>
                <div className="text-xs text-slate-400">{skipVoting ? 'Sem votação ao finalizar' : 'Jogadores avaliam após o jogo'}</div>
              </div>
            </div>
            <button type="button" onClick={() => setSkipVoting(!skipVoting)} className={`relative w-12 h-7 rounded-full transition-colors ${!skipVoting ? 'bg-amber-500' : 'bg-slate-300'}`}>
              <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${!skipVoting ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
          </div>
          <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Trophy className={`w-5 h-5 ${scoringEnabled ? 'text-amber-500' : 'text-slate-500'}`} />
              <div>
                <div className="text-sm font-medium text-slate-700">{scoringEnabled ? 'Pontuação Ativa' : 'Sem Pontuação'}</div>
                <div className="text-xs text-slate-400">{scoringEnabled ? 'Gera classificação mensal' : 'Sem tabela de classificação'}</div>
              </div>
            </div>
            <button type="button" onClick={() => setScoringEnabled(!scoringEnabled)} className={`relative w-12 h-7 rounded-full transition-colors ${scoringEnabled ? 'bg-amber-500' : 'bg-slate-300'}`}>
              <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${scoringEnabled ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
          </div>
          {scoringEnabled && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
              <p className="text-xs font-bold text-amber-700 uppercase tracking-wide">Pontos por resultado</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Vitória</label>
                  <NumericInput value={scoringWin} onChange={setScoringWin} min={0} max={99} className="w-full p-2 text-center bg-white border border-amber-200 rounded-lg outline-none text-sm font-bold" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Empate</label>
                  <NumericInput value={scoringDraw} onChange={setScoringDraw} min={0} max={99} className="w-full p-2 text-center bg-white border border-amber-200 rounded-lg outline-none text-sm font-bold" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Falta (-)</label>
                  <NumericInput value={scoringAbsence} onChange={setScoringAbsence} min={0} max={99} className="w-full p-2 text-center bg-white border border-amber-200 rounded-lg outline-none text-sm font-bold" />
                </div>
              </div>
              <p className="text-xs font-bold text-amber-700 uppercase tracking-wide pt-1">Zonas da tabela</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">🟢 Zona de liderança</label>
                  <NumericInput value={scoringLeaderZone} onChange={setScoringLeaderZone} min={0} max={10} className="w-full p-2 text-center bg-white border border-amber-200 rounded-lg outline-none text-sm font-bold" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">🔴 Zona de corte</label>
                  <NumericInput value={scoringRelegZone} onChange={setScoringRelegZone} min={0} max={10} className="w-full p-2 text-center bg-white border border-amber-200 rounded-lg outline-none text-sm font-bold" />
                </div>
              </div>
              <div className="flex items-center justify-between pt-1">
                <div>
                  <div className="text-sm font-medium text-slate-700">Exigir gols/assistências</div>
                  <div className="text-xs text-slate-400">Obriga registrar stats antes de finalizar</div>
                </div>
                <button type="button" onClick={() => setScoringRequireStats(!scoringRequireStats)} className={`relative w-12 h-7 rounded-full transition-colors ${scoringRequireStats ? 'bg-amber-500' : 'bg-slate-300'}`}>
                  <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${scoringRequireStats ? 'left-[22px]' : 'left-0.5'}`} />
                </button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-3 flex-1 min-w-0 mr-4">
              <div>
                <div className="text-sm font-medium text-slate-700">Abertura do checkin</div>
                <div className="text-xs text-slate-400">Horas antes do jogo (0 = sempre aberto)</div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <NumericInput value={checkinHoursBefore} onChange={setCheckinHoursBefore} min={0} max={168} className="w-16 p-2 text-center bg-white border border-slate-200 rounded-lg outline-none text-sm font-bold" />
              <span className="text-xs text-slate-400">h</span>
            </div>
          </div>
          <Button type="submit" className="w-full mt-4"><Save className="w-4 h-4" /> Salvar</Button>
        </form>

        {editingGroup && (
          <div className="mt-8 pt-6 border-t border-red-100">
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full flex items-center justify-center gap-2 p-3 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl transition-colors text-sm font-medium"
            >
              <Trash2 className="w-4 h-4" /> Excluir Grupo
            </button>
          </div>
        )}

        <Modal isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} title="Excluir Grupo?">
          <p className="text-sm text-slate-500 mb-6 text-center">
            Tem certeza que deseja excluir <strong>{editingGroup?.name}</strong>? Esta acao nao pode ser desfeita.
          </p>
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setShowDeleteConfirm(false)}>Cancelar</Button>
            <Button variant="danger" className="flex-1" onClick={async () => { await deleteGroup(editingGroup.id); navigate('/'); }}>
              <Trash2 className="w-4 h-4" /> Excluir
            </Button>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default GroupFormPage;
